/**
 * ChecklistAccordion — collapsible categories with tasks and questions.
 *
 * Layout-agnostic: the parent controls spacing and max-width.
 */

import { useState } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";
import { useTranslation } from "@/packages/utils";
import type { ChecklistTemplateData } from "./types";

interface ChecklistAccordionProps {
  data: ChecklistTemplateData;
}

export function ChecklistAccordion({ data }: ChecklistAccordionProps) {
  const { t } = useTranslation(["common"]);
  const [openCategory, setOpenCategory] = useState<number | null>(0);

  if (!data.categories || data.categories.length === 0) {
    return (
      <div className="rounded-xl border border-border bg-card p-6 text-center text-sm text-muted">
        {t("standards.noChecklistCategoriesFound")}
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-border overflow-hidden">
      {data.categories.map((category, catIdx) => {
        const isOpen = openCategory === catIdx;
        const taskCount = category.tasks?.length ?? 0;
        const questionCount =
          category.tasks?.reduce(
            (sum, t) => sum + (t.questions?.length ?? 0),
            0,
          ) ?? 0;

        return (
          <div
            key={category.name}
            className="border-b border-border/60 last:border-0"
          >
            {/* Category header */}
            <button
              type="button"
              onClick={() => setOpenCategory(isOpen ? null : catIdx)}
              className="w-full flex items-center justify-between px-4 py-3 bg-card hover:bg-card-hover transition-colors text-left"
            >
              <div className="flex items-center gap-3 min-w-0">
                {isOpen ? (
                  <ChevronDown size={16} className="text-muted shrink-0" />
                ) : (
                  <ChevronRight size={16} className="text-muted shrink-0" />
                )}
                <div className="min-w-0">
                  <span className="text-sm font-medium text-primary">
                    {category.name}
                  </span>
                  {category.clauseRefs && category.clauseRefs.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-1">
                      {category.clauseRefs.map((ref) => (
                        <span
                          key={ref}
                          className="inline-block rounded px-1.5 py-0.5 text-[10px] font-medium bg-accent/10 text-accent"
                        >
                          {ref}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-3 text-xs text-muted shrink-0 ml-4">
                <span>{taskCount} tasks</span>
                <span>{questionCount} questions</span>
              </div>
            </button>

            {/* Category body: tasks */}
            {isOpen && (
              <div className="border-t border-border/40 bg-card/30">
                {category.tasks?.map((task) => (
                  <div
                    key={task.description}
                    className="border-b border-border/30 last:border-0 px-6 py-3"
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="min-w-0">
                        <p className="text-sm text-primary">
                          {task.description}
                        </p>
                        {task.clauseRefs && task.clauseRefs.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-1.5">
                            {task.clauseRefs.map((ref) => (
                              <span
                                key={ref}
                                className="inline-block rounded px-1.5 py-0.5 text-[10px] font-medium bg-accent/10 text-accent"
                              >
                                {ref}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                      {task.required && (
                        <span className="shrink-0 text-[10px] font-medium rounded px-1.5 py-0.5 bg-red-500/10 text-red-500">
                          Required
                        </span>
                      )}
                    </div>

                    {/* Questions */}
                    {task.questions && task.questions.length > 0 && (
                      <div className="mt-2 space-y-1.5 pl-3 border-l-2 border-border/40">
                        {task.questions.map((q) => (
                          <div key={q.text} className="flex items-start gap-2">
                            <span className="mt-0.5 shrink-0 text-[10px] font-medium rounded px-1.5 py-0.5 bg-card border border-border/60 text-muted uppercase">
                              {q.type}
                            </span>
                            <p className="text-xs text-secondary">{q.text}</p>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
